/**
 * This module is a common utility for fetching customer's device fingerprint.
 *
 * Client JS library  - https://clientjs.org/
 *
 * Usage:
 * 1) To get the customer device-fingerprint, directly call `getFPforCustomParams`.
 *     by passing required browser params as comma separated string or array & localstorage flag
 *     Ex: FingerprintUtil.getFPforCustomParams('6,3,5', true);
 *
 * 2) To get the fingerprint stored in localstorage, directly call 'fetchPersistedLogin365FP'.
 *     Ex: FingerprintUtil.fetchPersistedLogin365FP();
 *
 * 3) To get the fingerprint parameter values used for computing fingerprinits, directly call 'getCustomFingerPrintMeta'.
 *     by passing required browser params as comma separated string or array.
 *     Ex: FingerprintUtil.getCustomFingerPrintMeta('2,8,9');
 */

{
  const { ClientJS } = require('clientjs');
  const ClientSideStorage = require('./ClientSideStorage');

  const LOGIN365_STORAGE_KEY = 'login365FingerPrint';
  const client = new ClientJS(); // eslint-disable-line no-undef

  const FingerprintUtil = {

    getCustomFingerPrintMeta(fingerprintPattern) {
      const fpParams = []; // denotes the fingerprint-params collected in a array.
      let targetParamArray = [];
      if (typeof fingerprintPattern === 'string') {
        targetParamArray = fingerprintPattern.split(',');
      } else if (Array.isArray(fingerprintPattern)) {
        targetParamArray = fingerprintPattern;
      } else {
        return fpParams;
      }

      const targetParamSet = new Set(targetParamArray.map(e => parseInt(e, 10)).sort((a, b) => (a - b)));
      if (targetParamSet.has(1)) { // Get browser useragent.
        fpParams.push(client.getUserAgent());
      }
      if (targetParamSet.has(2)) { // Get browser name.
        fpParams.push(client.getBrowser());
      }
      if (targetParamSet.has(3)) { // Get browser version.
        fpParams.push(client.getBrowserVersion());
      }
      if (targetParamSet.has(4)) { // Get browser major version.
        fpParams.push(client.getBrowserMajorVersion());
      }
      if (targetParamSet.has(5)) { // Get engine.
        fpParams.push(client.getEngine());
      }
      if (targetParamSet.has(6)) { // Get engine version.
        fpParams.push(client.getEngineVersion());
      }
      if (targetParamSet.has(7)) { // Get os name.
        fpParams.push(client.getOS());
      }
      if (targetParamSet.has(8)) { // Get os version.
        fpParams.push(client.getOSVersion());
      }
      if (targetParamSet.has(9)) { // Get Screen details.
        fpParams.push(client.getScreenPrint());
      }
      if (targetParamSet.has(10)) { // Get Color depth.
        fpParams.push(client.getColorDepth());
      }
      if (targetParamSet.has(11)) { // Get curr resolution.
        fpParams.push(client.getCurrentResolution());
      }
      if (targetParamSet.has(12)) { // Get available resolution.
        fpParams.push(client.getAvailableResolution());
      }
      if (targetParamSet.has(13)) { // Get available fonts.
        fpParams.push(client.getFonts());
      }
      if (targetParamSet.has(14)) { // Get time-zone.
        const time = new Date();
        if (this.isDST(time)) {
          const clientTimeZone = Math.abs(client.getTimeZone() - 1);
          // Convert back to string after adjusting for DST
          const appendZero = clientTimeZone.toString().padStart(2, '0');
          const currentZoneString = `-${appendZero}`;
          fpParams.push(currentZoneString);
        } else {
          fpParams.push(client.getTimeZone());
        }
      }
      if (targetParamSet.has(15)) { // Get language.
        fpParams.push(client.getLanguage());
      }
      if (targetParamSet.has(16)) { // Get Canvas element details.
        fpParams.push(client.getCanvasPrint());
      }
      return fpParams;
    },

    isDST(date) {
      const janOffset = new Date(date.getFullYear(), 0, 1).getTimezoneOffset();
      const julOffset = new Date(date.getFullYear(), 6, 1).getTimezoneOffset();
      return Math.max(janOffset, julOffset) !== date.getTimezoneOffset();
    },

    /*
      We have 7Params as of now for calculating device Fingerprint.
      os&version browser&version tiime&lang canvas
      ['7', '8', '2', '4', '14', '15', '16']
      customParams should be array-of-integers or comma separated integers.
      persistFp should be boolean -deciding whether to store the FP in localstorage.
     */
    getFPforCustomParams(customParams, persistFp) {
      const fpParams = this.getCustomFingerPrintMeta(customParams);
      const deviceFingerPrint = client.getCustomFingerprint(...fpParams);
      if (persistFp) {
        ClientSideStorage.setPersistent(LOGIN365_STORAGE_KEY, deviceFingerPrint);
      }
      return deviceFingerPrint;
    },

    fetchPersistedLogin365FP: () => ClientSideStorage.getPersistent(LOGIN365_STORAGE_KEY),
  };


  module.exports = FingerprintUtil;
}

